/**
 * The shared FAQ copy, with each centre's details filled in.
 *
 * All 28 clinic pages link to one FAQ entry in Contentful, so the questions are
 * written once and reused. Placeholders stand in for whatever differs per
 * centre. This lives on its own because two places need the same answer: the
 * page that renders the questions, and the structured data that tells Google
 * what those questions are. Building both from one function is what stops the
 * two drifting apart, which is exactly what happened when the markup was
 * maintained by hand.
 */

// "Carlsbad, CA" -> "Carlsbad", so sentences read naturally.
export const cityOf = name => (name || "").split(",")[0].trim()
export const stateOf = name => (name || "").split(",")[1]?.trim() || ""

/**
 * Fill a centre's details into the shared FAQ copy.
 *
 * All 28 clinic pages link to one FAQ entry in Contentful, so the six
 * questions are written once and reused everywhere. Placeholders stand in for
 * the parts that differ per centre: {CITY}, {ST}, and the therapies that
 * centre offers. Case is ignored, so {city} typed into the editor works.
 *
 * {DEV_THERAPIES} expands to the whole developmental therapy list. Physical
 * therapy is named at Carlsbad only. Music therapy is named at the four
 * California centres with therapists on site, and called out as telehealth at
 * the other California centres.
 *
 * {INSURERS} lists the payers this centre is in-network with, read from the
 * same source as the logo band on the page so the two cannot disagree.
 * {INSURERS_BEHAVIORAL} adds a sentence for payers covering behavioral health
 * only, and is empty where the centre has none.
 *
 * {MUSIC} and {INSURERS_BEHAVIORAL} sit tight against the text before them;
 * the replacement supplies its own leading space or comma.
 */
/**
 * The developmental therapies a centre offers, written out.
 *
 * Music therapy is named wherever it is offered, in person or by telehealth,
 * without saying which. The items here share one trailing "therapy", so a note
 * attached to the last of them reads as if it covered the whole list, and this
 * sentence continues into "medical care, and mental health counseling" where
 * that would be badly wrong. {MUSIC} carries the telehealth detail instead: its
 * items each carry their own "therapy", so a note there attaches to one thing.
 */
const devTherapies = (music, physical) => {
  const named = ["speech-language", "occupational"]
  if (physical) named.push("physical")
  if (music) named.push("music")

  const list =
    named.length === 2
      ? `${named[0]} and ${named[1]}`
      : `${named.slice(0, -1).join(", ")}, and ${named[named.length - 1]}`

  return `${list} therapy`
}

/**
 * Tidy punctuation a placeholder left stranded.
 *
 * {MUSIC} and {INSURERS_BEHAVIORAL} expand to nothing at most centres, so an
 * editor who types a full stop after one leaves a doubled stop on every page
 * that does not use it. An ellipsis is left alone.
 */
const tidyPunctuation = text =>
  text
    .replace(/(?<!\.)\.\.(?!\.)/g, ".")
    .replace(/\s+([.,])/g, "$1")
    .replace(/\s{2,}/g, " ")

const fillPlaceholders = (value, name, music, insurers, physical) =>
  tidyPunctuation(
    (value || "")
      .replace(/\{INSURERS\}/gi, insurers?.allServices || "")
      .replace(/\{INSURERS_BEHAVIORAL\}/gi, insurers?.behavioralOnly || "")
      .replace(/\{CITY\}/gi, cityOf(name))
      .replace(/\{ST\}/gi, stateOf(name))
      .replace(
        /\{MUSIC\}/gi,
        music === "onsite"
          ? ", music therapy"
          : music === "telehealth"
          ? ", music therapy offered via telehealth"
          : ""
      )
      .replace(/\{DEV_THERAPIES\}/gi, devTherapies(music, physical))
  )

/** Rewrite every piece of text in a rich text document through `fill`. */
const fillRichText = (node, fill) => {
  if (!node || typeof node !== "object") return node

  const next = { ...node }
  if (typeof next.value === "string") next.value = fill(next.value)
  if (Array.isArray(next.content)) {
    next.content = next.content.map(child => fillRichText(child, fill))
  }

  return next
}

/** All the text in a rich text document, for checking which placeholders it uses. */
const collectText = node => {
  if (!node || typeof node !== "object") return ""

  const own = typeof node.value === "string" ? node.value : ""
  const children = Array.isArray(node.content)
    ? node.content.map(child => collectText(child)).join(" ")
    : ""

  return `${own} ${children}`
}

const INSURER_PLACEHOLDER = /\{INSURERS(?:_BEHAVIORAL)?\}/i

const needsInsurers = item =>
  INSURER_PLACEHOLDER.test(
    `${item.title || ""} ${collectText(item.text?.json)}`
  )

/** The shared questions with this centre's city, therapies and payers in them. */
export const localiseFaqs = (questions, name, music, insurers, physical) => {
  const fill = value => fillPlaceholders(value, name, music, insurers, physical)

  return (
    (questions || [])
      // A centre with no payer list mapped cannot answer the insurance question,
      // so it is left off that page rather than shown with an empty list.
      .filter(item => insurers || !needsInsurers(item))
      .map(item => ({
        ...item,
        text: item.text?.json
          ? { ...item.text, json: fillRichText(item.text.json, fill) }
          : item.text,
        title: fill(item.title),
      }))
  )
}

/** Plain text of a rich text answer, for the structured data. */
const plainText = json =>
  ((json || {}).content || [])
    .filter(node => node.nodeType === "paragraph")
    .map(node =>
      (node.content || [])
        .map(child => child.value || "")
        .join("")
        .trim()
    )
    .filter(Boolean)
    .join(" ")

/**
 * The FAQPage node for a clinic, built from the questions the page renders.
 *
 * Every centre's SEO Metadata used to carry this by hand, and all 27 of them
 * had drifted: they still advertised three questions the pages stopped showing.
 * Generating it means the markup cannot say something the page does not.
 */
export const buildFaqSchema = questions => {
  const entries = (questions || [])
    .map(item => ({
      "@type": "Question",
      acceptedAnswer: { "@type": "Answer", text: plainText(item.text?.json) },
      name: item.title,
    }))
    .filter(entry => entry.name && entry.acceptedAnswer.text)

  return entries.length ? { "@type": "FAQPage", mainEntity: entries } : null
}

/**
 * Put the generated parts into the hand-written schema from Contentful.
 *
 * Everything else in that graph, the clinic's address, hours, services and
 * breadcrumbs, is left exactly as the editor wrote it. Only what code can keep
 * correct is replaced or added: the FAQ, a url that named an old slug, and the
 * list of accepted plans, which no entry carries and which cannot be read off
 * the logos.
 */
export const withGeneratedSchema = (
  schema,
  { canonicalUrl, clinicNode, keepService, payment, questions } = {}
) => {
  const faq = buildFaqSchema(questions)
  if (!schema) return schema

  // Most centres carry a hand-written @graph in their SEO Metadata, but not
  // all: dev has one, and a newly created clinic would have none. Wrapping a
  // plain schema in a graph means the generated parts do not depend on
  // somebody having pasted one in.
  const { "@context": context = "https://schema.org", ...rest } = schema
  const graph = Array.isArray(schema["@graph"]) ? schema["@graph"] : [rest]

  const isClinic = node =>
    /MedicalClinic|LocalBusiness/.test(String(node["@type"]))

  const next = graph
    .filter(node => !(faq && node["@type"] === "FAQPage"))
    .map(node => {
      if (!isClinic(node)) return node

      // The BHCOE badge is coming off the footer, so the claim comes out of
      // the markup with it. Twenty-seven entries assert it by hand; dropping
      // it here means nobody has to edit them, and pasting the old schema back
      // in cannot quietly restore it.
      const { accreditation, ...rest } = node
      void accreditation

      return {
        ...rest,
        // Every entry lists Music Therapy whether or not the centre offers it,
        // and none lists physical therapy. The page copy is filtered the same
        // way, so the markup has to agree with it.
        // A hand-written node wins where it has a value, but Tempe's lists no
        // services at all, so fall back to the generated list rather than
        // leaving that page without one. Either way it is filtered to what the
        // centre actually offers.
        ...(keepService
          ? {
              availableService: (Array.isArray(node.availableService) &&
              node.availableService.length
                ? node.availableService
                : clinicNode?.availableService || []
              ).filter(service => keepService(service?.name || "")),
            }
          : {}),
        ...(payment ? { paymentAccepted: payment } : {}),
      }
    })
    .map(node =>
      canonicalUrl && node.url && node.url.includes("/locations/")
        ? {
            ...node,
            ...(node["@id"]
              ? {
                  "@id": `${canonicalUrl}${
                    node["@id"].split("#")[1]
                      ? `#${node["@id"].split("#")[1]}`
                      : ""
                  }`,
                }
              : {}),
            url: canonicalUrl,
          }
        : node
    )

  // A hand-written graph is used in place of the generated one, so an entry
  // that pasted in only a FAQ and breadcrumbs loses its clinic block
  // altogether. Braintree's does exactly that. Add the generated one back so
  // every location page describes itself as a MedicalClinic.
  const withClinic = next.some(isClinic)
    ? next
    : [
        ...next,
        ...(clinicNode
          ? [
              (({ "@context": ignored, ...node }) => ({
                ...node,
                ...(keepService && Array.isArray(node.availableService)
                  ? {
                      availableService: node.availableService.filter(service =>
                        keepService(service?.name || "")
                      ),
                    }
                  : {}),
                ...(payment ? { paymentAccepted: payment } : {}),
                ...(canonicalUrl ? { url: canonicalUrl } : {}),
              }))(clinicNode),
            ]
          : []),
      ]

  return {
    "@context": context,
    "@graph": faq ? [...withClinic, faq] : withClinic,
  }
}
